import type { AppCtx } from '../App';
import { api } from '../api';
import type { NotificationView } from '../api';
import { pendingRemark } from '../design-model/pending';
import { FUNCTION_SECTION, isFunction } from '../functions/functions';

/*
 * Куда ведёт уведомление. Одно место на два экрана — колокольчик и чат
 * уведомлений: перечень типов должен вести себя одинаково в обоих, иначе одно
 * и то же событие открывает разное.
 */
/**
 * Возможность, в которую ведёт уведомление (коды — server/src/capabilities.ts).
 * Нужна, чтобы решить, открывается ли событие с телефона: выключенная
 * возможность делает карточку тихой.
 */
export function targetCapability(n: NotificationView): string {
  if (n.type === 'task') return 'section.projects.sprints';
  if (n.type === 'calendar_event' || n.type === 'calendar_reminder') return 'block.calendar';
  if (n.type === 'call_summary') return 'block.chat';
  // «Вас добавили в команду» ведёт в работу: раздел — функции этой работы (у
  // генподряда — проекты). На телефоне открывается только генподряд — у прочих
  // функций раздел там выключен, и карточка остаётся тихой.
  if (n.type === 'team_added') return isFunction(n.fn) ? FUNCTION_SECTION[n.fn] : 'section.projects';
  // Задания ведут в проверку подрядчика, а она этап процесса заведения: с
  // телефона этот процесс не открывается (реестр доступов, 12.09.2026).
  if (n.type.startsWith('assignment_')) return 'section.contractors.tasks';
  // Возврат, подпись, новая версия, отмена, ответ в ветке замечаний и вход
  // подрядчика по ссылке — события карточки подрядчика во вкладке «Договоры».
  if (n.type.startsWith('contract_')) return 'section.contractors.contracts';
  if (n.proc) return 'section.kb.regulations';
  // Упоминание в обсуждении замечания по модели — вкладка «Модель» работы генпроектировщика;
  // на телефоне раздел выключен, и карточка остаётся тихой.
  if (n.thread?.startsWith('model-remark:')) return 'section.design';
  return 'block.chat';
}

/**
 * Событие, которое с телефона не открыть: карточка только отмечает прочтение
 * и никуда не ведёт (дизайн-система 3.49).
 */
export function isSilent(app: AppCtx, n: NotificationView, mobile: boolean): boolean {
  return mobile && app.mobileMode(targetCapability(n)) === 'NONE';
}

export function openNotification(app: AppCtx, n: NotificationView): void {
  if (n.type === 'task' && n.projectId) {
    app.openProject(n.projectId, 'gpr');
    return;
  }
  if (n.type === 'team_added' && n.projectId) {
    // Участник или наблюдатель — по профилю: сначала свежий, иначе только что
    // добавленный открыл бы работу наблюдателем.
    const id = n.projectId;
    void app.refreshProfile().then(() => app.openProject(id));
    return;
  }
  if (n.type === 'calendar_event' || n.type === 'calendar_reminder') {
    // Идентификатор встречи уже есть в уведомлении — раздел откроет карточку
    // сразу, без промежуточного захода в общий список.
    if (n.eventId) app.openCalendarEvent(n.eventId, n.startsAt);
    else app.showView('calendar');
    return;
  }
  if (n.type === 'call_summary' && n.callId) {
    app.openCallSummary(n.callId);
    return;
  }
  if (n.type.startsWith('assignment_') && n.assignmentId) {
    // Уведомление ведёт в саму работу, а не в карточку рядом с ней: какая
    // проверка заведена по заданию, знает сервер — на клиенте её не угадать.
    // У договора работа лежит на процессной вкладке (3.60): задание требует
    // действия, а действия живут там.
    const id = n.assignmentId;
    void api
      .assignment(id)
      .then((a) => {
        if (a.targetType === 'contract')
          return api.contract(a.targetId).then((c) => app.openContract(c.party.profileId, c.id, 'approval'));
        app.openContractor(a.targetId, a.checkId ?? undefined);
      })
      .catch(() => app.showView('tasks'));
    return;
  }
  if (
    n.type === 'contract_returned' || n.type === 'contract_signed' ||
    n.type === 'contract_version' || n.type === 'contract_cancelled' ||
    n.type === 'contract_comment_reply' || n.type === 'contract_guest_joined' ||
    n.type === 'contract_uk_signing' || n.type === 'contract_uk_reply'
  ) {
    // Договор без подрядчика (продажа, прочее) пишет пустой profileId: он
    // открывается в «Реестре договоров».
    if (n.contractId) app.openContract(n.profileId || null, n.contractId);
    else app.showView('tasks');
    return;
  }
  if (n.thread?.startsWith('model-remark:') && n.projectId) {
    // Карточку замечания откроет сама вкладка — при показе или сразу, если уже открыта.
    pendingRemark.set(n.thread.slice('model-remark:'.length), n.projectId);
    app.openProject(n.projectId, 'model');
    return;
  }
  if (n.proc && app.procMap[n.proc]) app.openProc(n.proc, n.commentId);
}
